import React, { useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  Select,
  Stepper,
  Text,
  createDatePickerValidator,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { ApplicationActionName } from '~/types';
import {
  ProgramType,
  generateIntakeOptions,
  useApplication,
  useLegacyApplicationRequirements,
  useIntakes,
  useLegacyApplication,
  useApplicationAvailableActions,
} from '../../data';
import { ArrowLeftOutlineIcon, ArrowRightOutlineIcon } from '@applyboard/ui-icons';
import { validateEslIntake, getApplicationStatusMessage } from '../../utils';
import { IntakeInfoCard } from '~/components/IntakeInfoCard';
import { isEqual } from 'lodash';
import { useLegacySendApplicationAction } from '../../data/actions';
import { SuccessMessage } from '~/components/SuccessMessage';
import { TimelessDateStringInput } from '~/components/TimelessDateStringInput';
import {
  ApplicationRequirementDeadline,
  RequirementDeadlinesTable,
  getRelevantRequirements,
} from '~/components/RequirementDeadlinesTable';

interface FieldValues {
  eslIntake: string;
  academicIntake: string;
  eslActualStartDate: string;
  eslActualAlternateStartDate: string;
  academicActualStartDate: string;
  academicActualAlternateStartDate: string;
  requirementDeadlines: ApplicationRequirementDeadline[];
}

const { Form, Field, useFieldValues, useTriggerValidation } = createForm<FieldValues>();

const maxDate = new Date();
maxDate.setFullYear(maxDate.getFullYear() + 5);
const minDate = new Date();
minDate.setFullYear(minDate.getFullYear() - 5);

interface ProcessDeferralModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  deferralAction: DeferralAction | null;
  onSuccess: (params: {
    academicIntakeId: string;
    academicIntakeDate: string;
    academicActualStartDate: string | null;
    academicAlternateStartDate: string | null;
    eslIntakeId: string | null;
    eslIntakeDate: string | null;
    eslActualStartDate: string | null;
    eslAlternateStartDate: string | null;
  }) => void;
  onDismissSuccess: () => void;
}

export const ProcessDeferralModal = (props: ProcessDeferralModalProps) => {
  if (!props.isOpen) {
    return null;
  }

  return <ProcessDeferralModalContents {...props} />;
};

type DeferralAction = Extract<
  ApplicationActionName,
  ApplicationActionName.CANCEL_DEFERRAL | ApplicationActionName.FINISH_DEFERRAL
>;

const ProcessDeferralModalContents = (props: ProcessDeferralModalProps) => {
  const [step, setStep] = useState(props.deferralAction ? 2 : 1);
  const [deferralAction, setDeferralAction] = useState<DeferralAction | null>(props.deferralAction);
  const formId = useId();
  const { legacyApplication } = useLegacyApplication();
  const { applicationRequirements } = useLegacyApplicationRequirements();
  const { eslIntakes, academicIntakes } = useIntakes();
  const { sendApplicationAction, isSendingApplicationAction } = useLegacySendApplicationAction();
  const toasts = useToasts();

  const academicProgram = legacyApplication.programs.find(
    program => program.programType === ProgramType.Main,
  );
  const eslProgram = legacyApplication.programs.find(
    program => program.programType === ProgramType.Esl,
  );

  const requirementsWithDeadlines = getRelevantRequirements(applicationRequirements);

  // TODO: use intakeId directly instead of these finds once https://applyboard.slack.com/archives/C04PKSZ1F39/p1698168618738749 is resolved
  const currentEslIntakeId = eslIntakes
    .find(intake => intake.startDate === eslProgram?.intake.startDate)
    ?.intakeId.toString();
  const currentAcademicIntakeId = academicIntakes
    .find(intake => intake.startDate === academicProgram?.intake.startDate)
    ?.intakeId.toString();

  if (!academicProgram) {
    throw new Error('Could not find academic program');
  }

  if (eslProgram?.intake.startDate && !currentEslIntakeId) {
    throw new Error('Could not find current esl intake in list of intakes');
  }

  if (academicProgram?.intake.startDate && !currentAcademicIntakeId) {
    throw new Error('Could not find current academic intake in list of intakes');
  }

  const defaultValues = {
    eslIntake: currentEslIntakeId || '',
    academicIntake: currentAcademicIntakeId || '',
    eslActualStartDate: eslProgram?.actualStartDate || '',
    eslActualAlternateStartDate: eslProgram?.alternativeStartDate || '',
    academicActualStartDate: academicProgram?.actualStartDate || '',
    academicActualAlternateStartDate: academicProgram?.alternativeStartDate || '',
    requirementDeadlines: requirementsWithDeadlines.map(requirement => ({
      requirementId: requirement.id,
      deadline: requirement.deadline || '',
    })),
  };

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={isOpen => {
        if (step === 4) {
          props.onDismissSuccess();
        }

        props.onOpenChange(isOpen);
      }}
      size={step === 1 || step === 4 ? 'md' : 'lg'}
    >
      <Dialog.Heading>
        <DialogTitle step={step} deferralAction={deferralAction} />
      </Dialog.Heading>
      <Dialog.Content>
        {step < 4 && (
          <Form
            id={formId}
            defaultValues={defaultValues}
            onSubmit={values => {
              if (!deferralAction) return;

              const intakeInfoChanged =
                values.academicIntake !== defaultValues.academicIntake ||
                values.academicActualAlternateStartDate !==
                  defaultValues.academicActualAlternateStartDate ||
                values.academicActualStartDate !== defaultValues.academicActualStartDate ||
                values.eslIntake !== defaultValues.eslIntake ||
                values.eslActualAlternateStartDate !== defaultValues.eslActualAlternateStartDate ||
                values.eslActualStartDate !== defaultValues.eslActualStartDate;

              sendApplicationAction(
                {
                  actionId: deferralAction,
                  actionData: {
                    academicProgramId: intakeInfoChanged
                      ? academicProgram.programId.toString()
                      : undefined,
                    academicIntake: intakeInfoChanged ? values.academicIntake || null : undefined,
                    academicActualStartDate: intakeInfoChanged
                      ? values.academicActualStartDate || null
                      : undefined,
                    academicActualAlternateStartDate: intakeInfoChanged
                      ? values.academicActualAlternateStartDate || null
                      : undefined,

                    eslProgramId: intakeInfoChanged ? eslProgram?.programId.toString() : undefined,
                    eslIntake: intakeInfoChanged ? values.eslIntake || null : undefined,
                    eslActualStartDate: intakeInfoChanged
                      ? values.eslActualStartDate || null
                      : undefined,
                    eslActualAlternateStartDate: intakeInfoChanged
                      ? values.eslActualAlternateStartDate || null
                      : undefined,

                    requirementDeadlines: values.requirementDeadlines
                      .filter(item => {
                        const requirement = requirementsWithDeadlines.find(
                          r => r.id === item.requirementId,
                        );

                        return item.deadline !== requirement?.deadline;
                      })
                      .map(item => {
                        return {
                          ...item,
                          deadline: item.deadline || null,
                        };
                      }),
                  },
                },
                {
                  onSuccess: () => {
                    const academicIntakeDate =
                      academicIntakes.find(intake => intake.intakeId === values.academicIntake)
                        ?.startDate || null;
                    const eslIntakeDate =
                      (values.eslIntake &&
                        eslIntakes.find(intake => intake.intakeId === values.eslIntake)
                          ?.startDate) ||
                      null;
                    if (!academicIntakeDate) {
                      throw new Error('Could not find academic intake date');
                    }

                    if (values.eslIntake && !eslIntakeDate) {
                      throw new Error('Could not find esl intake date');
                    }

                    props.onSuccess({
                      academicIntakeId: values.academicIntake,
                      academicIntakeDate: academicIntakeDate,
                      academicActualStartDate: values.academicActualStartDate || null,
                      academicAlternateStartDate: values.academicActualAlternateStartDate || null,
                      eslIntakeId: values.eslIntake || null,
                      eslIntakeDate: eslIntakeDate || null,
                      eslActualStartDate: values.eslActualStartDate || null,
                      eslAlternateStartDate: values.eslActualAlternateStartDate || null,
                    });

                    setStep(4);
                  },
                  onError: err => {
                    toasts.negative(err instanceof Error ? err : new Error('Unknown error'));
                  },
                },
              );
            }}
          >
            {step === 1 && <FinishOrCancelDeferralStep />}
            {step === 2 && <UpdateIntakesStep deferralAction={deferralAction} />}
            {step === 2 && <UpdateIntakesStepButtons setStep={setStep} />}
            {step === 3 && (
              <UpdateRequirementDeadlinesStep
                deferralAction={deferralAction}
                defaultValues={defaultValues}
              />
            )}
          </Form>
        )}
        {step === 4 && <SuccessMessageStep />}
      </Dialog.Content>
      <Dialog.Actions>
        {step === 1 && (
          <FinishOrCancelDeferralActions
            onCancel={() => {
              setDeferralAction(ApplicationActionName.CANCEL_DEFERRAL);
              setStep(2);
            }}
            onFinish={() => {
              setDeferralAction(ApplicationActionName.FINISH_DEFERRAL);
              setStep(2);
            }}
          />
        )}
        {step === 3 && (
          <>
            <Dialog.CloseButton>Cancel</Dialog.CloseButton>
            <Flex gap={2}>
              <Button
                intent="primary"
                leadIcon={ArrowLeftOutlineIcon}
                emphasis="outlined"
                onClick={() => {
                  setStep(2);
                }}
              >
                Previous
              </Button>
              <Button
                intent="primary"
                type="submit"
                form={formId}
                loading={isSendingApplicationAction}
              >
                {deferralAction === 'cancel_deferral' ? 'Cancel deferral' : 'Finish deferral'}
              </Button>
            </Flex>
          </>
        )}
        {step === 4 && (
          <Button
            intent="primary"
            width="fill"
            onClick={() => {
              props.onDismissSuccess();
              props.onOpenChange(false);
            }}
          >
            OK
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
};

function DialogTitle({
  step,
  deferralAction,
}: {
  step: number;
  deferralAction: DeferralAction | null;
}) {
  if (step === 1) {
    return <>Finish or Cancel Deferral?</>;
  }

  if (step === 2 || step === 3) {
    return <>{deferralAction === 'cancel_deferral' ? 'Cancel Deferral' : 'Finish Deferral'}</>;
  }

  if (step === 4) {
    return (
      <>
        {deferralAction === 'cancel_deferral'
          ? 'Deferral Successfully Cancelled'
          : 'Deferral Successfully Approved'}
      </>
    );
  }

  return null;
}

function SuccessMessageStep() {
  const { application } = useApplication();
  const { legacyApplication } = useLegacyApplication();

  return (
    <SuccessMessage>
      <Text variant="bodyM" intent="secondary">
        Application&apos;s status has been successfully changed to{' '}
        {getApplicationStatusMessage(legacyApplication.status, application.paidAt)}!
      </Text>
      <Text variant="bodyM" intent="secondary">
        It&apos;s recommended to check the Compare Requirements view now to make sure all necessary
        requirements are active and available for the applicant.
      </Text>
    </SuccessMessage>
  );
}

const EslInfoCard = ({ hideDates }: { hideDates?: boolean }) => {
  const { eslIntakes } = useIntakes();
  const { eslIntake } = useFieldValues(['eslIntake']);

  return (
    <IntakeInfoCard
      aria-label="ESL intake information"
      intakes={eslIntakes}
      intakeId={eslIntake}
      hideDates={hideDates}
    />
  );
};

const AcademicInfoCard = ({ hideDates }: { hideDates?: boolean }) => {
  const { academicIntakes } = useIntakes();
  const { academicIntake } = useFieldValues(['academicIntake']);

  return (
    <IntakeInfoCard
      aria-label="Academic intake information"
      intakes={academicIntakes}
      intakeId={academicIntake}
      hideDates={hideDates}
    />
  );
};

const UpdateIntakesStep = ({ deferralAction }: { deferralAction: DeferralAction | null }) => {
  const { getApplicationAvailableAction } = useApplicationAvailableActions();
  const { legacyApplication } = useLegacyApplication();
  const { eslIntakes, academicIntakes } = useIntakes();

  const academicProgram = legacyApplication.programs.find(
    program => program.programType === ProgramType.Main,
  );
  const eslProgram = legacyApplication.programs.find(
    program => program.programType === ProgramType.Esl,
  );

  if (deferralAction === null) {
    return null;
  }

  const action = getApplicationAvailableAction(ApplicationActionName.FINISH_DEFERRAL);
  const showStartDates = action?.additionalInformation?.startDateUpdateAllowed;

  return (
    <Flex direction="column" gap={4}>
      <ProcessDeferralStepper step={1} deferralAction={deferralAction} />
      <Text>
        {deferralAction === 'cancel_deferral'
          ? 'You’re about to cancel the deferral request. Please select program intakes to be able to move forward with your request.'
          : 'You’re about to finish the deferral request. Please select program intakes to be able to move forward with your request.'}
      </Text>
      <Flex direction="column" gap={2}>
        <Field
          as={Select}
          label="ESL intake"
          name="eslIntake"
          validate={(value, { values, triggerValidation }) => {
            const eslIntakeStartDate =
              eslIntakes.find(intake => intake.intakeId === value)?.startDate || '';
            const academicIntakeStartDate =
              academicIntakes.find(intake => intake.intakeId === values.academicIntake)
                ?.startDate || '';

            triggerValidation('eslActualStartDate');
            const isEslIntakeValid = validateEslIntake({
              eslProgram,
              eslIntake: eslIntakeStartDate,
              academicIntake: academicIntakeStartDate,
            });
            return isEslIntakeValid;
          }}
          disabled={!eslProgram}
          appearance="styled"
        >
          {generateIntakeOptions({
            selectedIntake: eslProgram?.intake.startDate,
            intakes: eslIntakes,
            useIds: true,
          }).map(({ label, value }, index) => (
            <Select.Option key={index} label={label} value={value} />
          ))}
        </Field>
        {showStartDates ? (
          <Flex direction="column" gap={4}>
            <EslInfoCard hideDates />
            <Flex direction="row" justify="between" gap={4}>
              <Field
                as={TimelessDateStringInput}
                label="ESL start date"
                name="eslActualStartDate"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                size="lg"
                disabled={!eslProgram}
                validate={(value, { values }) => {
                  if (values.eslIntake !== '' && value === '') {
                    return 'ESL actual start date cannot be left empty since an ESL intake is currently specified.';
                  }
                  if (value === '') {
                    return true;
                  }
                  return validateDatePicked(value);
                }}
              />
              <Field
                as={TimelessDateStringInput}
                label="Alternative ESL start date"
                name="eslActualAlternateStartDate"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                size="lg"
                disabled={!eslProgram}
              />
            </Flex>
          </Flex>
        ) : (
          <EslInfoCard />
        )}
      </Flex>
      <Flex direction="column" gap={2}>
        <Field
          as={Select}
          label="Academic intake"
          name="academicIntake"
          appearance="styled"
          validate={(value, { triggerValidation }) => {
            triggerValidation('eslIntake');
            return true;
          }}
          required="Please enter an academic intake"
        >
          {generateIntakeOptions({
            selectedIntake: academicProgram?.intake.startDate,
            intakes: academicIntakes,
            useIds: true,
          }).map(({ label, value }, index) => (
            <Select.Option key={index} label={label} value={value} />
          ))}
        </Field>
        {showStartDates ? (
          <Flex direction="column" gap={4}>
            <AcademicInfoCard hideDates />
            <Flex direction="row" justify="between" gap={4}>
              <Field
                as={TimelessDateStringInput}
                label="Academic start date"
                name="academicActualStartDate"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                size="lg"
                validate={validateDatePicked}
                required="Please enter an Academic start date"
              />
              <Field
                as={TimelessDateStringInput}
                label="Alternative academic start date"
                name="academicActualAlternateStartDate"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                size="lg"
              />
            </Flex>
          </Flex>
        ) : (
          <AcademicInfoCard />
        )}
      </Flex>
    </Flex>
  );
};

const UpdateIntakesStepButtons = ({ setStep }: { setStep: (step: number) => void }) => {
  const triggerValidation = useTriggerValidation();

  return (
    // remove the <div> once we have proper Form validation in Dialogs - https://applyboard.atlassian.net/browse/UIE-1687
    // can make these part of the Dialog footer again as well
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '24px',
        marginBottom: '-24px',
      }}
    >
      <Dialog.CloseButton>Cancel</Dialog.CloseButton>
      <Button
        intent="primary"
        trailIcon={ArrowRightOutlineIcon}
        onClick={async () => {
          if (await triggerValidation()) {
            setStep(3);
          }
        }}
      >
        Next
      </Button>
    </div>
  );
};

const FinishOrCancelDeferralStep = () => {
  return (
    <Flex direction="column" gap={4}>
      <Text>
        You’re about to either finish or cancel the deferral request. Keep in mind, once a decision
        is made you won’t be able to undo it.
      </Text>
      <Text>Do you want to finish or cancel the deferral?</Text>
    </Flex>
  );
};

const FinishOrCancelDeferralActions = ({
  onCancel,
  onFinish,
}: {
  onCancel: () => void;
  onFinish: () => void;
}) => {
  const { canPerformApplicationAction } = useApplicationAvailableActions();
  return (
    <>
      <Button
        intent="negative"
        emphasis="outlined"
        width="fill"
        onClick={onCancel}
        disabled={!canPerformApplicationAction(ApplicationActionName.CANCEL_DEFERRAL)}
      >
        Cancel deferral
      </Button>
      <Button
        intent="primary"
        width="fill"
        onClick={onFinish}
        disabled={!canPerformApplicationAction(ApplicationActionName.FINISH_DEFERRAL)}
      >
        Finish deferral
      </Button>
    </>
  );
};

const ProcessDeferralStepper = ({
  step,
  deferralAction,
}: {
  step: number;
  deferralAction: DeferralAction | null;
}) => {
  return (
    <Stepper
      activeStep={step - 1}
      aria-label={`${deferralAction === 'finish_deferral' ? 'Finish' : 'Cancel'} deferral steps`}
    >
      <Stepper.Step.Title>Intakes</Stepper.Step.Title>
      <Stepper.Step.Title>Requirement deadlines</Stepper.Step.Title>
    </Stepper>
  );
};

const UpdateRequirementDeadlinesStep = ({
  deferralAction,
  defaultValues,
}: {
  deferralAction: DeferralAction | null;
  defaultValues: FieldValues;
}) => {
  const { legacyApplication } = useLegacyApplication();

  const academicProgram = legacyApplication.programs.find(
    program => program.programType === ProgramType.Main,
  );

  return (
    <Flex direction="column" gap={4}>
      <ProcessDeferralStepper step={2} deferralAction={deferralAction} />
      <Text>
        Please update the requirement deadline(s) based on the deferral LOA for the program &ldquo;
        {academicProgram?.programName}&rdquo; below.
      </Text>
      <Field
        as={RequirementDeadlinesTable}
        name="requirementDeadlines"
        validate={value => {
          for (const deadlineDate of value) {
            if (isEqual(value, defaultValues.requirementDeadlines)) {
              return true;
            }

            if (validateDatePicked(deadlineDate.deadline) !== true) {
              return 'All deadline dates must be within 5 years of today';
            }
          }
          return true;
        }}
        minDate={minDate}
        maxDate={maxDate}
      />
    </Flex>
  );
};

const validateDatePicked = createDatePickerValidator(minDate, maxDate, 'day');
